/**
 * Component that displays a profile/channel in composer channel selection
 */
import type * as React from 'react'
import { useCallback, memo } from 'react'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import type { ChannelType } from '@buffer-mono/popcorn'

import { ToggleChannels } from './ToggleChannels'

import AppActionCreators from '../../action-creators/AppActionCreators'
import { Services } from '../../AppConstants'
import {
  getCurrentPage,
  getSegmentChannelMetadata,
} from '../../utils/TrackingUtils'

import type { ComposerProfile } from '../../stores/types'
import styles from './ChannelItem.module.css'

type ChannelItemProps = {
  profile: ComposerProfile
  organizationId?: string | null
  isSelected: boolean
  tooltipContent: string
}

const ChannelItem = ({
  profile,
  organizationId,
  isSelected,
  tooltipContent,
}: ChannelItemProps): JSX.Element => {
  const hasSubprofiles = useCallback(() => {
    const {
      service: { name },
    } = profile
    const service = Services.get(name)
    return service?.hasSubprofiles || false
  }, [profile])

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const metadata = getSegmentChannelMetadata({
        profile,
        organizationId,
        page: getCurrentPage(),
      })

      if (isSelected) {
        // prevent dropdown from being expanded
        if (hasSubprofiles()) e.stopPropagation()
        AppActionCreators.unselectProfile(profile.id)
        BufferTracker.channelDeselected(metadata)
      } else {
        AppActionCreators.selectProfile(profile.id)
        // Select the first board for Pinterest
        // TODO: Remove this once we have a way to select a default board
        if (hasSubprofiles()) {
          AppActionCreators.selectSubprofile(
            profile.id,
            profile.subprofiles[0].id,
          )
        }
        BufferTracker.channelSelected(metadata)
      }

      e.preventDefault()
    },
    [profile, organizationId, hasSubprofiles, isSelected],
  )

  return (
    <ToggleChannels.Item
      value={profile.id}
      className={styles.channelItem}
      onClick={onClick}
      isSelected={isSelected}
      tooltipContent={tooltipContent}
      src={profile.isFake ? undefined : profile.images.avatar}
      alt={`${profile.service.username} ${profile.service.name} ${profile.serviceType}`}
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      channel={profile.service.name as ChannelType}
    />
  )
}

export default memo(ChannelItem, (prevProps, nextProps) => {
  return (
    prevProps.profile.id === nextProps.profile.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.profile.isDisabled === nextProps.profile.isDisabled &&
    prevProps.tooltipContent === nextProps.tooltipContent &&
    prevProps.organizationId === nextProps.organizationId
  )
})
